import { FirebaseRepositoryService } from './../../firebase-repository.service';

import { Component, NgZone, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import * as firebaseui from 'firebaseui';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthFirebaseService } from '../../auth-firebase.service';
import { NbToastrService } from '@nebular/theme';
import { applyActionCode } from 'firebase/auth';
import * as firebase from 'firebase/compat/app';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ngx-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  hashInvitation: string;
  accepted: Boolean;
  isVerifyingEmail: boolean = false;
  private destroy$ = new Subject<void>();
  private firebaseUiInitialized = false;

  constructor(
    private authService: AuthFirebaseService, 
    private repoService: FirebaseRepositoryService,
    private router: Router, 
    private ngZone: NgZone, 
    private route: ActivatedRoute, 
    private nbToastr: NbToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Verificar si hay parámetros de query para verificación de email
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(queryParams => {
      const mode = queryParams.get('mode');
      const oobCode = queryParams.get('oobCode');

      if (mode === 'verifyEmail' && oobCode) {
        this.handleEmailVerification(oobCode);
        return;
      }
    });

    // Manejar parámetros de ruta para invitaciones
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.hashInvitation = params.get('hash');
    });
  }

  /**
   * AfterViewInit garantiza que el DOM esté completamente renderizado
   * antes de inicializar FirebaseUI
   */
  ngAfterViewInit(): void {
    // Pequeño delay para asegurar que el DOM está listo
    setTimeout(() => {
      this.initializeFirebaseAuth();
    }, 100);
  }

  /**
   * Inicializa la autenticación de Firebase
   */
  private initializeFirebaseAuth(): void {
    if (this.firebaseUiInitialized) {
      return;
    }

    this.ngZone.run(() => {
      this.authService.initAuthUi('#firebaseui-auth-container')
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          async (user) => {
            console.log("Tenemos usuario:", user);
            
            if (this.hashInvitation && this.hashInvitation != '') {
              console.log("Aceptando invitación:", this.hashInvitation);
              try {
                await this.repoService.acceptInvitation(this.hashInvitation);
                this.accepted = true;
                this.nbToastr.success('Se ha aceptado la invitación con éxito.');
              } catch (err) {
                console.error("Error al aceptar invitación:", err);
                this.nbToastr.danger('Ha ocurrido un error al aceptar la invitación');
              }
            } else {
              this.gotoHome();
            }
          },
          (error) => {
            console.error("Error en initAuthUi:", error);
          }
        );
    });

    this.firebaseUiInitialized = true;
  }

  /**
   * Maneja la verificación de email cuando Firebase envía el oobCode
   * @param oobCode Código de verificación enviado por Firebase
   */
  private handleEmailVerification(oobCode: string): void {
    this.isVerifyingEmail = true;
    console.log('Procesando verificación de email con código:', oobCode);

    applyActionCode(firebase.default.auth(), oobCode)
      .then(() => {
        console.log('Email verificado exitosamente');
        this.nbToastr.success('¡Email verificado correctamente! Por favor, inicia sesión.');
        // Redirigir al login después de 2 segundos para que el usuario vea el mensaje
        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        }, 2000);
      })
      .catch((error) => {
        console.error('Error al verificar email:', error);
        let errorMessage = 'Error al verificar el email';

        // Manejar errores específicos de Firebase
        switch (error.code) {
          case 'auth/invalid-action-code':
            errorMessage = 'El código de verificación es inválido o ha expirado.';
            break;
          case 'auth/expired-action-code':
            errorMessage = 'El código de verificación ha expirado. Por favor, solicita uno nuevo.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'La cuenta de usuario ha sido deshabilitada.';
            break;
          default:
            errorMessage = `Error: ${error.message}`;
        }

        this.nbToastr.danger(errorMessage);
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        }, 3000);
      })
      .finally(() => {
        this.isVerifyingEmail = false;
        this.cdr.markForCheck();
      });
  }

  gotoHome() {
    this.router.navigate(['']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
